import { ref } from 'vue';
import { fetchInboxSummary, fetchWorkflowInbox } from '@/api/workflowApi';
import { isAnnualAppraisalsEnabled } from '@/constants/featureFlags';

function normalizeSummaryPayload(data) {
  if (isAnnualAppraisalsEnabled()) {
    const count = Number(data?.pending_item_count ?? 0);
    return {
      pendingCount: Number.isFinite(count) ? Math.max(0, count) : 0,
      breakdown:
        data?.breakdown && typeof data.breakdown === 'object' ? data.breakdown : {}
    };
  }

  const breakdown =
    data?.breakdown && typeof data.breakdown === 'object'
      ? { ...data.breakdown }
      : {};
  const appraisalCount = Number(breakdown.annual_appraisal ?? 0);
  delete breakdown.annual_appraisal;

  const rawCount = Number(data?.pending_item_count ?? 0);
  const pendingCount = Number.isFinite(rawCount)
    ? Math.max(0, rawCount - (Number.isFinite(appraisalCount) ? appraisalCount : 0))
    : Object.values(breakdown).reduce(
        (sum, value) => sum + (Number.isFinite(Number(value)) ? Number(value) : 0),
        0
      );

  return { pendingCount, breakdown };
}

const pendingCount = ref(0);
const completedCount = ref(null);
const breakdown = ref({});
const loadFailed = ref(false);

let inflightPromise = null;
let pendingInvalidation = false;
let generation = 0;

async function fetchSummary(seq) {
  try {
    const [data, historyData] = await Promise.all([
      fetchInboxSummary(),
      fetchWorkflowInbox({ tab: 'completed', page: 1, page_size: 1, due_state: 'all' }).catch(() => null)
    ]);
    if (seq !== generation) return;
    const normalized = normalizeSummaryPayload(data);
    pendingCount.value = normalized.pendingCount;
    breakdown.value = normalized.breakdown;
    if (historyData && typeof historyData.total === 'number') {
      completedCount.value = Math.max(0, historyData.total);
    }
    loadFailed.value = false;
  } catch (error) {
    if (seq !== generation) return;
    loadFailed.value = true;
    console.warn('Failed to load inbox summary:', error);
  }
}

function runRefresh() {
  const seq = generation;
  const promise = (async () => {
    do {
      pendingInvalidation = false;
      await fetchSummary(seq);
    } while (seq === generation && pendingInvalidation);
  })().finally(() => {
    if (inflightPromise === promise) inflightPromise = null;
  });
  inflightPromise = promise;
  return inflightPromise;
}

/**
 * Module-level inbox summary state (sidebar badge + action invalidation).
 */
export function useInboxSummary() {
  async function refresh() {
    return inflightPromise || runRefresh();
  }

  function invalidate() {
    // Ordinary readers share the current fetch. Only a mutation needs another
    // read when the response already in flight may predate that mutation.
    if (inflightPromise) pendingInvalidation = true;
    else void refresh();
  }

  function reset() {
    generation += 1;
    inflightPromise = null;
    pendingInvalidation = false;
    pendingCount.value = 0;
    completedCount.value = null;
    breakdown.value = {};
    loadFailed.value = false;
  }

  return {
    pendingCount,
    completedCount,
    breakdown,
    loadFailed,
    refresh,
    invalidate,
    reset
  };
}
